import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import {
	HlrRatesCreateResponse,
	HlrRatesDeleteRequest,
	HlrRatesDeleteResponse,
	HlrRatesReadRequest,
	HlrRatesReadResponse,
	HlrRatesUpdateResponse
} from '@helpers/types/hlr';
import { AbstractCRUDService, AlarisApiService, AlarisLocalStorageService } from '@campaign-portal/components-library';
import { HlrRates } from '@helpers/types/models';
import { Filter, RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import { ReadRequest, ReadResponse } from '@campaign-portal/namespace/common/implementations';
import { STORAGE } from '@helpers/types/consts';

@Injectable({
	providedIn: 'root'
})
export class RatesService implements AbstractCRUDService {


	readonly entity = '';
	readonly title = '';
	readonly loading$ = new BehaviorSubject<boolean>(false);
	readonly create = this.update;

	constructor(
		private readonly api: AlarisApiService,
		private readonly storage: AlarisLocalStorageService
	) {
	}

	read(params: ReadRequest): Observable<ReadResponse<HlrRates[]>> {
		const parsedParams: HlrRatesReadRequest = this.prepareParams(params);
		return this.api.loader<HlrRatesReadResponse>('Rates.Read', parsedParams, this.loading$)
			.pipe(map((resp) => {
				return {
					Success: !!resp.data,
					Total: resp.length,
					Data: resp.data
				};
			}));
	}

	update(params: {
		id: number | null;
		currencyId: number | null;
		startDate: string | undefined;
		rate: number | null
	}): Observable<HlrRatesUpdateResponse | HlrRatesCreateResponse> {
		const method = params.id ? 'Rates.Update' : 'Rates.Create';
		return this.api.loader<HlrRatesUpdateResponse | HlrRatesCreateResponse>(method, params, this.loading$);
	}

	delete(params: HlrRatesDeleteRequest): Observable<HlrRatesDeleteResponse>{
		return this.api.loader<HlrRatesDeleteResponse>('Rates.Delete', params, this.loading$);
	}

	private prepareParams(params: RPCRequestParams): HlrRatesReadRequest {
		return {
			offset: params.Paging?.Skip ?? 0,
			limit: params.Paging?.Take ?? parseInt(this.storage.get(STORAGE.PAGE_SIZE) ?? '25', 10),
			active: 'exchange_rate_id',
			direction: 'asc',
			filters: params.Filters
				? params.Filters.map((filter) => {
					return this.parseFilterField(filter);
				})
				: []
		};
	}

	private parseFilterField(filter: Filter): { key: string, value: string } {
		const variable = filter.Field;
		const value: any = filter.Value;
		switch (variable) {
			case 'currencyName':
				return { key: 'currency_name', value: value + '' };
			case 'startDate':
				return { key: 'start_date',
					value: (value.Start?.toString() || '') + ',' + (value.End?.toString() || '')};
			case 'rate':
				return { key: 'rate', value: value + '' };
			default:
				return { key: variable, value: value + '' };
		}
	}
}
